// TenantQuotasView.jsx
// Admin tab for inspecting + editing per-tenant quotas. Type a plantId to
// view its current quota + usage; switch tier or override individual fields
// (storage bytes, max alarms, query rate, etc).

import { useEffect, useState } from 'react';
import { Gauge, RefreshCw, Save, Factory } from 'lucide-react';
import tenantQuotaService from '../services/tenantQuotaService.js';

const C = {
  pageBg: '#0b0f19', cardBg: '#0f172a', border: '#1e293b', input: '#020617',
  text: '#e5e7eb', muted: '#9ca3af', accent: '#a78bfa',
  good: '#22c55e', warn: '#f59e0b', bad: '#ef4444'
};
const ipt = {
  padding: '6px 10px', background: C.input, border: `1px solid ${C.border}`,
  borderRadius: 6, color: C.text, fontSize: 12, outline: 'none'
};
const btn = { ...ipt, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };

// Friendly labels for the quota fields
const FIELD_DEFS = [
  { key: 'maxAlarmRules',           label: 'Max alarm rules',                unit: 'count' },
  { key: 'maxScheduledReports',     label: 'Max scheduled reports',          unit: 'count' },
  { key: 'maxDashboards',           label: 'Max dashboards',                 unit: 'count' },
  { key: 'maxConcurrentExports',    label: 'Max concurrent exports',         unit: 'count' },
  { key: 'maxRunningExportsPerUser',label: 'Max running exports per user',   unit: 'count' },
  { key: 'maxGridfsStorageBytes',   label: 'GridFS storage cap',             unit: 'bytes' },
  { key: 'maxExportFileSizeBytes',  label: 'Max single export file size',    unit: 'bytes' },
  { key: 'queriesPerMinute',        label: 'Queries per minute',             unit: 'rate' },
  { key: 'concurrentQueries',       label: 'Concurrent queries',             unit: 'count' },
  { key: 'alarmEvalIntervalMs',     label: 'Alarm eval interval',            unit: 'ms' },
  { key: 'notificationsPerHour',    label: 'Notifications per hour',         unit: 'rate' }
];

function fmt(value, unit) {
  if (value == null) return '—';
  if (unit === 'bytes') {
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
    return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
  if (unit === 'ms') {
    if (value < 1000) return `${value} ms`;
    if (value < 60000) return `${(value / 1000).toFixed(1)} s`;
    return `${(value / 60000).toFixed(1)} min`;
  }
  return String(value);
}

export default function TenantQuotasView() {
  const [plantId, setPlantId] = useState('plantA');
  const [quota, setQuota] = useState(null);     // resolved (defaults + overrides merged)
  const [usage, setUsage] = useState(null);
  const [tiers, setTiers] = useState({});       // tier defaults from server
  const [overrides, setOverrides] = useState({});  // local edits, sent to PUT
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    (async () => {
      try { setTiers(await tenantQuotaService.tiers()); } catch (e) { /* tiers endpoint may need admin */ }
    })();
  }, []);

  async function loadQuota() {
    if (!plantId) return;
    setLoading(true); setError(null);
    try {
      const res = await tenantQuotaService.get(plantId);
      setQuota(res.quota);
      setUsage(res.usage);
      // Start the override form blank — admin can opt in to override specific fields
      setOverrides({ tier: res.quota?.tier || 'free' });
    } catch (err) { setError(err.message); setQuota(null); setUsage(null); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadQuota(); }, [plantId]);

  function setOverride(key, value) {
    setOverrides(o => {
      const next = { ...o };
      if (value === '' || value == null) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      // Clean: numbers stay numbers, blanks get omitted
      const body = {};
      for (const [k, v] of Object.entries(overrides)) {
        if (v === '' || v == null) continue;
        if (k === 'tier' || k === 'notes') body[k] = v;
        else body[k] = parseInt(v, 10);
      }
      await tenantQuotaService.save(plantId, body);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      await loadQuota();   // re-fetch resolved state
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ padding: 20, background: C.pageBg, color: C.text, minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 22 }}>
            <Gauge size={20} /> Tenant quotas
          </h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            Per-tenant limits on alarms, exports, storage, query rate. Defaults come from the tenant's tier.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Factory size={16} style={{ color: C.muted }} />
          <input style={{ ...ipt, minWidth: 180 }}
                 value={plantId}
                 placeholder="plantId"
                 onChange={(e) => setPlantId(e.target.value)} />
          <button style={btn} onClick={loadQuota}><RefreshCw size={14} /></button>
        </div>
      </div>

      {error && <div style={errBox}>{error}</div>}

      {!quota ? (
        <div style={emptyState}>
          {loading ? 'Loading…' : 'Enter a plantId to view its quota.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Left: resolved quota + usage */}
          <Card title={`Current — tier "${quota.tier}"`}>
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={th}>Field</th>
                  <th style={th}>Limit</th>
                  <th style={th}>In use</th>
                </tr>
              </thead>
              <tbody>
                {FIELD_DEFS.map(def => {
                  const limit = quota[def.key];
                  const used  = usage ? usageFor(def.key, usage) : null;
                  const pct   = (limit && used != null) ? Math.round((used / limit) * 100) : null;
                  return (
                    <tr key={def.key}>
                      <td style={td}>{def.label}</td>
                      <td style={tdMono}>{fmt(limit, def.unit)}</td>
                      <td style={tdMono}>
                        {used != null
                          ? <span style={pctColor(pct)}>{fmt(used, def.unit)}{pct != null ? `  (${pct}%)` : ''}</span>
                          : <span style={{ color: C.muted }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {usage && (
              <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
                Usage measured live: {usage.alarms || 0} alarms, {usage.dashboards || 0} dashboards,
                {' '}{usage.exportsRunning || 0} exports running, {usage.fileCount || 0} files
                ({fmt(usage.gridfsStorageBytes || 0, 'bytes')}).
              </div>
            )}
          </Card>

          {/* Right: editable overrides */}
          <Card title="Override">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Row label="Tier">
                <select style={ipt} value={overrides.tier || 'free'}
                        onChange={(e) => setOverride('tier', e.target.value)}>
                  <option value="free">free</option>
                  <option value="pro">pro</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </Row>
              {FIELD_DEFS.map(def => (
                <Row key={def.key} label={def.label}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input style={{ ...ipt, flex: 1 }} type="number" min="0"
                           placeholder={`tier default: ${fmt(tierDefault(tiers, overrides.tier, def.key), def.unit)}`}
                           value={overrides[def.key] ?? ''}
                           onChange={(e) => setOverride(def.key, e.target.value)} />
                    <span style={{ fontSize: 11, color: C.muted, minWidth: 50 }}>{def.unit}</span>
                  </div>
                </Row>
              ))}
              <Row label="Notes">
                <input style={ipt} type="text" value={overrides.notes ?? ''}
                       placeholder="(optional admin note)"
                       onChange={(e) => setOverride('notes', e.target.value)} />
              </Row>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                {savedFlash && <span style={{ color: C.good, fontSize: 12 }}>✓ Saved</span>}
                <button style={{ ...btn, background: C.accent, color: '#fff', borderColor: C.accent }}
                        onClick={save} disabled={saving}>
                  <Save size={12} /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 14, lineHeight: 1.5 }}>
              Leave a field blank to use the tier default. Numeric inputs save as integers.
              The tier selector becomes the fallback for any unset field.
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────

// Map a quota field name to the corresponding usage measurement
function usageFor(key, u) {
  switch (key) {
    case 'maxAlarmRules':           return u.alarms;
    case 'maxScheduledReports':     return u.scheduled;
    case 'maxDashboards':           return u.dashboards;
    case 'maxConcurrentExports':    return u.exportsRunning;
    case 'maxRunningExportsPerUser':return null;
    case 'maxGridfsStorageBytes':   return u.gridfsStorageBytes;
    default:                        return null;
  }
}

function tierDefault(tiers, tier, key) {
  return tiers?.[tier || 'free']?.[key];
}

function pctColor(pct) {
  if (pct == null) return {};
  if (pct >= 90) return { color: C.bad };
  if (pct >= 70) return { color: C.warn };
  return { color: C.text };
}

function Card({ title, children }) {
  return (
    <div style={{ padding: 14, background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14, color: C.text }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10, alignItems: 'center' }}>
      <label style={{ color: C.muted, fontSize: 12 }}>{label}</label>
      {children}
    </div>
  );
}

const errBox     = { padding: 8, background: 'rgba(239,68,68,0.1)', color: '#fca5a5', borderRadius: 6, fontSize: 12, border: '1px solid rgba(239,68,68,0.3)', marginBottom: 12 };
const emptyState = { padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 8 };
const tbl        = { width: '100%', borderCollapse: 'collapse', fontSize: 12, color: C.text };
const th         = { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${C.border}`, color: C.muted, fontWeight: 600 };
const td         = { padding: '6px 8px', borderBottom: `1px solid ${C.border}` };
const tdMono     = { ...td, fontFamily: 'monospace' };
